import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { PasswordInput } from './ui/password-input';
import { CheckCircle, AlertTriangle, Loader2, Building2, Mail, User, Globe, CreditCard } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface SignupPageProps {
  onSignupComplete: (subdomain: string) => void;
}

export function SignupPage({ onSignupComplete }: SignupPageProps) {
  const [step, setStep] = useState<'info' | 'payment' | 'processing' | 'complete'>('info');
  
  // Company Info
  const [companyName, setCompanyName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [password, setPassword] = useState('');
  
  // Subdomain validation
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [subdomainError, setSubdomainError] = useState<string | null>(null);
  
  // Payment
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Pricing
  const MONTHLY_PRICE = 299; // $299/month

  // Validate subdomain on change
  useEffect(() => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailable(null);
      setSubdomainError(null);
      return;
    }

    const checkAvailability = async () => {
      setCheckingSubdomain(true);
      setSubdomainError(null);

      try {
        // Validate format first
        const subdomainRegex = /^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$/;
        if (!subdomainRegex.test(subdomain)) {
          setSubdomainAvailable(false);
          setSubdomainError('Invalid format. Use lowercase letters, numbers, and hyphens only.');
          setCheckingSubdomain(false);
          return;
        }

        // Check if reserved (import from utils)
        const { isReservedSubdomain } = await import('../utils/reservedSubdomains');
        if (isReservedSubdomain(subdomain)) {
          setSubdomainAvailable(false);
          setSubdomainError('This subdomain is reserved.');
          setCheckingSubdomain(false);
          return;
        }

        // Check availability via API
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/public/check-subdomain/${subdomain}`,
          {
            headers: {
              ...bearerAuthHeader()
            }
          }
        );

        const data = await response.json();

        if (data.available) {
          setSubdomainAvailable(true);
        } else {
          setSubdomainAvailable(false);
          setSubdomainError(data.error || 'Subdomain not available');
        }
      } catch (err) {
        setSubdomainError('Failed to check availability');
      } finally {
        setCheckingSubdomain(false);
      }
    };

    const debounce = setTimeout(checkAvailability, 500);
    return () => clearTimeout(debounce);
  }, [subdomain]);

  const handleCompanyInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (!companyName || !subdomain || !adminEmail || !adminName || !password) {
      setError('All fields are required');
      return;
    }

    if (subdomainAvailable !== true) {
      setError('Please choose an available subdomain');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // Move to payment step
    setStep('payment');
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate card info
      if (!cardNumber || !cardExpiry || !cardCvc) {
        throw new Error('Please enter complete payment information');
      }

      // NOTE: In production, you would use Stripe.js to tokenize the card
      // For now, we'll simulate the payment process
      
      setStep('processing');

      // Call signup API
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/public/signup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...bearerAuthHeader()
          },
          body: JSON.stringify({
            companyName,
            subdomain,
            adminEmail,
            adminName,
            password,
            // In production, send Stripe token instead
            paymentToken: 'tok_visa' // Placeholder
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Success!
      setSuccess(true);
      setStep('complete');

      // Redirect to subdomain after 3 seconds
      setTimeout(() => {
        onSignupComplete(subdomain);
      }, 3000);

    } catch (err: any) {
      setError(err.message);
      setStep('payment');
    } finally {
      setLoading(false);
    }
  };

  const formatSubdomain = (value: string) => {
    return value.toLowerCase().replace(/[^a-z0-9-]/g, '');
  };

  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
        <Card className="max-w-2xl w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-3xl">Welcome to PathKit</CardTitle>
            <CardDescription>
              Your account has been created successfully
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-lg mb-2">Your PathKit URL</h3>
              <p className="text-2xl text-blue-600 font-mono mb-4">
                {subdomain}.pathkit.pro
              </p>
              <p className="text-sm text-gray-600">
                We've sent a verification email to <strong>{adminEmail}</strong> with your login credentials and getting started guide.
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold">What's Next?</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                <li>Check your email for the welcome message</li>
                <li>Log in to your PathKit instance</li>
                <li>Complete the initial setup wizard</li>
                <li>Start configuring your service desk</li>
              </ol>
            </div>

            <Alert>
              <AlertDescription>
                Redirecting you to your PathKit instance in a few seconds...
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin text-blue-600" />
            <h3 className="text-xl font-semibold mb-2">Setting up your account...</h3>
            <p className="text-gray-600 text-sm">
              We're creating your company, configuring your subdomain, and setting up your admin account. This will just take a moment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl mb-2">Start Your PathKit Trial</h1>
          <p className="text-xl text-gray-600">
            Configure Jira Service Management faster than ever before
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  {step === 'info' ? 'Company Information' : 'Payment Details'}
                </CardTitle>
                <CardDescription>
                  {step === 'info' 
                    ? 'Tell us about your consulting firm' 
                    : 'Start your 14-day free trial (cancel anytime)'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {step === 'info' ? (
                  <form onSubmit={handleCompanyInfoSubmit} className="space-y-4">
                    {/* Company Name */}
                    <div>
                      <Label htmlFor="companyName">
                        <Building2 className="w-4 h-4 inline mr-2" />
                        Company Name
                      </Label>
                      <Input
                        id="companyName"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Acme Consulting"
                        required
                      />
                    </div>

                    {/* Subdomain */}
                    <div>
                      <Label htmlFor="subdomain">
                        <Globe className="w-4 h-4 inline mr-2" />
                        Your PathKit URL
                      </Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          id="subdomain"
                          value={subdomain}
                          onChange={(e) => setSubdomain(formatSubdomain(e.target.value))}
                          placeholder="acme"
                          required
                          className="flex-1"
                        />
                        <span className="text-gray-600 text-sm">.pathkit.pro</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        This will be your dedicated login URL
                      </p>

                      {checkingSubdomain && (
                        <p className="text-sm text-gray-600 mt-2 flex items-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Checking availability...
                        </p>
                      )}

                      {subdomainAvailable === true && (
                        <div className="flex items-center gap-2 text-sm text-green-600 mt-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>Available</span>
                        </div>
                      )}

                      {subdomainAvailable === false && subdomainError && (
                        <div className="flex items-center gap-2 text-sm text-red-600 mt-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span>{subdomainError}</span>
                        </div>
                      )}
                    </div>

                    {/* Admin Name */}
                    <div>
                      <Label htmlFor="adminName">
                        <User className="w-4 h-4 inline mr-2" />
                        Your Name
                      </Label>
                      <Input
                        id="adminName"
                        value={adminName}
                        onChange={(e) => setAdminName(e.target.value)}
                        placeholder="John Smith"
                        required
                      />
                    </div>

                    {/* Admin Email */}
                    <div>
                      <Label htmlFor="adminEmail">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Your Email
                      </Label>
                      <Input
                        id="adminEmail"
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        placeholder="john@acmeconsulting.com"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        You'll use this to log in
                      </p>
                    </div>

                    {/* Password */}
                    <div>
                      <Label htmlFor="password">
                        Password
                      </Label>
                      <PasswordInput
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        required
                      />
                    </div>

                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full" 
                      size="lg"
                      disabled={subdomainAvailable !== true}
                    >
                      Continue to Payment
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handlePaymentSubmit} className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
                      <p className="text-sm text-gray-700">
                        <strong>14-day free trial</strong> • Then ${MONTHLY_PRICE}/month
                      </p>
                    </div>

                    {/* Card Number */}
                    <div>
                      <Label htmlFor="cardNumber">
                        <CreditCard className="w-4 h-4 inline mr-2" />
                        Card Number
                      </Label>
                      <Input
                        id="cardNumber"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        placeholder="4242 4242 4242 4242"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Expiry */}
                      <div>
                        <Label htmlFor="cardExpiry">Expiry Date</Label>
                        <Input
                          id="cardExpiry"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(e.target.value)}
                          placeholder="MM/YY"
                          required
                        />
                      </div>

                      {/* CVC */}
                      <div>
                        <Label htmlFor="cardCvc">CVC</Label>
                        <Input
                          id="cardCvc"
                          value={cardCvc}
                          onChange={(e) => setCardCvc(e.target.value)}
                          placeholder="123"
                          required
                        />
                      </div>
                    </div>

                    <Alert>
                      <AlertDescription className="text-xs">
                        Your card will not be charged during the 14-day trial. Cancel anytime.
                      </AlertDescription>
                    </Alert>

                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="flex gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setStep('info')}
                        className="flex-1"
                      >
                        Back
                      </Button>
                      <Button 
                        type="submit" 
                        className="flex-1" 
                        size="lg"
                        disabled={loading}
                      >
                        {loading ? 'Processing...' : 'Start Free Trial'}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* What's Included */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What's Included</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Unlimited projects & workflows</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Jira backlog generation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>SOW tracking & change orders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Team collaboration tools</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Dedicated support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Trust Signals */}
            <Card>
              <CardContent className="p-4 text-center text-sm text-gray-600">
                <p className="mb-2">Secure and Encrypted</p>
                <p>Cancel anytime, no questions asked</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 mt-8">
          By signing up, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}